import {
  AnalysisSetupStepFeatureTitles as FeatureTitles,
  AnalysisSetupStepSectionDescriptions as SectionDescriptions,
  AnalysisSetupStepSectionTitles as SectionTitles,
} from "@pages/platform-analysis-v2/_id/steps/setup/constants";
import { AnalysisSetupOwnerField } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/owner";
import { AnalysisSetupNameField } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/analysis-name";
import { AnalysisSetupFamilyGroupField } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/family-group";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  AnalysisSetupDTO,
  SetupStepRenderSectionConfig,
  AnalysisSetupStepTitles,
} from "@models/platform-analysis/entities/steps/setup";
import { AnalysisSetupStepFamilyMembersField } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/family-members";
import { AnalysisSetupStepObjectivesField } from "./components/rendered-fields/objectives";
import { AnalysisSetupStepTargetAdviserField } from "./components/rendered-fields/target-adviser";
import { AnalysisSetupStepFeatureTemplateSelection } from "./components/rendered-fields/feature-template-selection";
import { AnalysisSetupStepRateCardsSelection } from "./components/rendered-fields/rate-cards-selection";
import { AnalysisSetupStepSelectionField } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/steps-selection";
import { AnalysisSetupSupplierMainPlatformsSelection } from "./components/rendered-fields/supplier-main-platform-selection";
import { AnalysisSetupSupplierOtherPlatformSelection } from "@pages/platform-analysis-v2/_id/steps/setup/components/rendered-fields/supplier-other-platform-selection";
import { AnalysisSetupAdviserMainPlatformsSelection } from "./components/rendered-fields/adviser-main-platform";
import { AnalysisSetupAdviserOtherPlatformSelection } from "./components/rendered-fields/adviser-other-platform";

export const AnalysisSetupStepSectionsConfig: SetupStepRenderSectionConfig[] = [
  {
    name: "Details",
    showFor: ["supplier", "adviser"],
    titles: {
      main: SectionTitles.Details,
      subTitle: SectionDescriptions.Details,
    },
    fields: [
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupOwnerField,
      },
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupNameField,
      },
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupStepSelectionField,
      },
    ],
  },
  {
    name: "Family members",
    showFor: ["adviser"],
    titles: {
      main: SectionTitles.FamilyMembers,
      subTitle: SectionDescriptions.FamilyMembers,
    },
    fields: [
      {
        showFor: ["adviser"],
        renderComponent: AnalysisSetupFamilyGroupField,
      },
      {
        showFor: ["adviser"],
        renderComponent: AnalysisSetupStepFamilyMembersField,
      },
    ],
  },
  {
    name: "Situation and objectives",
    showFor: ["supplier", "adviser"],
    titles: {
      main: SectionTitles.SituationAndObjectives,
      subTitle: SectionDescriptions.SituationAndObjectives,
    },
    fields: [
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupStepObjectivesField,
      },
    ],
  },
  {
    name: "Target adviser",
    showFor: ["supplier"],
    specificPermissions: ["isSupplierBdmSale"],
    titles: {
      main: SectionTitles.TargetAdviser,
      subTitle: SectionDescriptions.TargetAdviser,
    },
    fields: [
      {
        showFor: ["supplier"],
        renderComponent: AnalysisSetupStepTargetAdviserField,
      },
    ],
  },
  {
    name: "Supplier main platform",
    showFor: ["supplier"],
    titles: {
      main: SectionTitles.MainPlatform,
      subTitle: SectionDescriptions.MainPlatform,
    },
    fields: [
      {
        showFor: ["supplier"],
        renderComponent: AnalysisSetupSupplierMainPlatformsSelection,
      },
    ],
  },
  {
    name: "Adviser main platform",
    showFor: ["adviser"],
    titles: {
      main: SectionTitles.MainPlatform,
      subTitle: SectionDescriptions.MainPlatform,
    },
    fields: [
      {
        showFor: ["adviser"],
        renderComponent: AnalysisSetupAdviserMainPlatformsSelection,
      },
    ],
  },
  {
    name: "Supplier other platforms",
    showFor: ["supplier"],
    titles: {
      main: SectionTitles.OtherPlatforms,
      subTitle: SectionDescriptions.OtherPlatform,
    },
    fields: [
      {
        showFor: ["supplier"],
        renderComponent: AnalysisSetupSupplierOtherPlatformSelection,
      },
    ],
  },
  {
    name: "Adviser other platforms",
    showFor: ["adviser"],
    titles: {
      main: SectionTitles.OtherPlatforms,
      subTitle: SectionDescriptions.OtherPlatform,
    },
    fields: [
      {
        showFor: ["adviser"],
        renderComponent: AnalysisSetupAdviserOtherPlatformSelection,
      },
    ],
  },
  {
    name: "Load template",
    dependencyField: "hasFeatureStep",
    showFor: ["supplier", "adviser"],
    titles: {
      main: SectionTitles.LoadTemplate,
      subTitle: SectionDescriptions.LoadTemplate,
    },
    fields: [
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupStepFeatureTemplateSelection,
      },
    ],
  },
  {
    name: "Rate cards",
    dependencyField: "hasFeeStep",
    showFor: ["supplier", "adviser"],
    titles: {
      main: SectionTitles.RateCards,
      subTitle: SectionDescriptions.RateCards,
    },
    fields: [
      {
        showFor: ["supplier", "adviser"],
        renderComponent: AnalysisSetupStepRateCardsSelection,
      },
    ],
  },
];

export const AnalysisFeatureSelectionsConfig: {
  step: AnalysisStep;
  key: keyof AnalysisSetupDTO;
  defaultValue: { supplier: boolean; adviser: boolean };
  allowModify: { supplier: boolean; adviser: boolean };
  titles: AnalysisSetupStepTitles;
}[] = [
  {
    step: AnalysisStep.Feature,
    key: "hasFeatureStep",
    defaultValue: { supplier: false, adviser: true },
    allowModify: { supplier: true, adviser: false },
    titles: FeatureTitles.FeatureStep,
  },
  {
    step: AnalysisStep.BusinessMetric,
    key: "hasBusinessMetricStep",
    defaultValue: { supplier: false, adviser: true },
    allowModify: { supplier: true, adviser: true },
    titles: FeatureTitles.BusinessMetricStep,
  },
  {
    step: AnalysisStep.Fee,
    key: "hasFeeStep",
    defaultValue: { supplier: false, adviser: true },
    allowModify: { supplier: true, adviser: true },
    titles: FeatureTitles.FeeStep,
  },
];
